import type { BreadcrumbProps } from '@blueprintjs/core'
import { observer } from 'mobx-react-lite'
import React, { useMemo } from 'react'
import { Filters, PerformanceFilters, TableName } from '../../config'
import { useKdpFilters } from '../../hooks/useKdpFilters'
import { useUser } from '../../hooks/useUser'
import type { ProductAdPerformance } from '../../types/product_ad_extended'
import type { TableProps } from '../ad_groups/AdGroupsTable'
import { addExtraCols, spliceAfter } from '../campaigns/CampaignsTable'
import { Layout } from '../Layout'
import {
    Columns,
    defaultPerfColumns,
    type ExpandedPerformanceMetrics,
} from '../table2/ad_grid_columns'
import { type AgGridColumnDef, AgGridTable } from '../table2/AgGridTable'
import { OverviewCharts } from '../../pages/dashboard-overview-charts'
import { useAdManagerCustomFields } from '../../features/custom-fields/ad-manager'

const defaultFilters = [
    Filters.PortfolioId,
    Filters.CampaignId,
    Filters.StateWithSplit,
    Filters.DeliveryStatus,
    Filters.DeliveryReason,
    Filters.TargetACOS,
    ...PerformanceFilters,
]

const mutableFields: string[] = ['state']

export const ProductAdsTable = observer(
    ({
        nested,
        store,
        savedFiltersStore,
        tableHooks,
        showCharts = true,
    }: TableProps<ProductAdPerformance & ExpandedPerformanceMetrics>): JSX.Element => {
        const useUserValues = useUser()
        const filters = useKdpFilters(defaultFilters)
        const customFields = useAdManagerCustomFields<
            ProductAdPerformance & ExpandedPerformanceMetrics
        >({
            profileId: useUserValues.profile === null ? null : String(useUserValues.profile),
            entityType: 'ad',
            entityIdKey: 'ad_id',
            store,
            reload: tableHooks.reload,
        })

        const breadcrumbs: BreadcrumbProps[] = [{ text: 'Manage' }]

        const cols3 = useMemo(() => {
            return [...newColumns(useUserValues.is_kdp), ...customFields.columns]
        }, [customFields.columns, useUserValues.is_kdp])

        return (
            <Layout
                breadcrumbs={breadcrumbs}
                primary={TableName.ProductAds}
                nested={nested}
                params={tableHooks.params}
            >
                {showCharts && (
                    <OverviewCharts
                        params={tableHooks.params}
                        className="mb-4"
                        chartType="product-ads"
                    />
                )}
                <AgGridTable
                    name={TableName.ProductAds}
                    columns={cols3}
                    tableHooks={tableHooks}
                    store={store}
                    showHeader={false}
                    savedFiltersStore={savedFiltersStore}
                    filters={filters}
                    selectKey="ad_id"
                    stableRowId
                    search
                    searchPlaceholder="Search by ASIN, Campaign or Ad Group name"
                    columnSelection
                    mutableFields={mutableFields}
                    bulkActions
                    otherActionsLeft={customFields.bulkAction}
                    nested={nested}
                />
            </Layout>
        )
    },
)

const newColumns = <T,>(isKdp: boolean): AgGridColumnDef<T>[] => {
    const cols: AgGridColumnDef<T>[] = [
        Columns.Select,
        Columns.ASIN,
        Columns.Parent,
        Columns.BidStrategy,
        Columns.PlacementBidAdjustments,
        ...defaultPerfColumns,
    ]

    spliceAfter('campaign_name', cols, Columns.DeliveryStatus)
    addExtraCols(cols, isKdp)

    return cols
}
